/**
 * @typedef {object} QueryFormOption
 * @property {string} name
 * @property {string} value
 * @property {boolean} [disabled]
 */
class AvailableTime {
	/**
	 * @param {number} [date]
	 */
	constructor(date) {
		if (!date) {
			this.value = new Date();
			this.value.setHours(0, 0, 0, 0);
		} else {
			this.value = new Date(date);
		}
		this.compareValues = [this.value.getHours(), this.value.getMinutes()];
	}

	/**
	 * @param {Date} date
	 * @returns {boolean}
	 */
	compareTimes(date) {
		const [thisHour, thisMinute] = this.compareValues;
		const currentHour = date.getHours();
		const currentMinute = date.getMinutes();

		if (currentHour === thisHour && currentMinute > thisMinute) {
			return true;
		}

		return currentHour > thisHour;
	}

	/**
	 * Creates a new `AvailableTime` instance, advanced 30 minutes
	 * @param {number} step The next increment to advance
	 * @returns {AvailableTime} The next 30 minute chunk
	 * */
	increment(step) {
		const next30 = this.value.getTime() + step * 30 * 60 * 1000;
		const clone = new AvailableTime(next30);
		return clone;
	}

	/**
	 * Returns self as either a 24-hour or 12-hour formatted timestamp (e.g. 7:00 PM)
	 * @param {boolean} [hour12]
	 * @returns {string} Formatted time string
	 */
    format(hour12 = true) {
        const hours24 = this.value.getHours();
        const minutes = this.value.getMinutes();
    
        if (!hour12) {
            return `${String(hours24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        }
    
        const suffix = hours24 >= 12 ? "PM" : "AM";
        let hours12 = hours24 % 12;
    
        if (hours12 === 0) hours12 = 12;
    
        return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${suffix}`;
    }
}


/**
 * Generates a 30-minute increment array of <option> elements.
 * Today = past times disabled
 * Future dates = all times enabled
 * Removes times between 2:00 AM and 7:59 AM
 */
export function generateTimeOptions(date) {
	const result = [];
	const now = new Date();

	// normalize dates to midnight
	const today = new Date(now);
	today.setHours(0, 0, 0, 0);

	const selectedDate = new Date(date);
	selectedDate.setHours(0, 0, 0, 0);

	const isToday = selectedDate.getTime() === today.getTime();

	const startTime = new AvailableTime(); // starts at midnight

	for (let i = 0; i < 48; i++) {
		const time = startTime.increment(i);

		// Remove early times: 2:00 AM to 7:59 AM
		const hour = time.value.getHours();
        if (hour < 8) {
			continue;
		}

		const value = time.format(false);
		const name = time.format();

		let isDisabled = false;

		if (isToday) {
			isDisabled = time.compareTimes(now);
		}

		result.push({
			name,
			value,
			disabled: isDisabled,
		});
	}

	return result;
}


export function getDefaultDate() {
	const today = new Date();

	return `${today.getFullYear()}-${String(
		today.getMonth() + 1
	).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
}

export function getDefaultTime() {
	const now = new Date();

	let hours = now.getHours();
	let minutes = now.getMinutes();

	// If before noon, default to 7:00 PM
	if (hours < 12) {
		now.setHours(19, 0, 0, 0);
	} else {
		// Round up to next half hour
		if (minutes === 0) {
			minutes = 0;
		} else if (minutes <= 30) {
			minutes = 30;
		} else {
			hours += 1;
			minutes = 0;
		}

		// Handle midnight rollover
		if (hours === 24) {
			hours = 0;
		}

		now.setHours(hours, minutes, 0, 0);
	}

	return now.toLocaleTimeString([], {
		hour12: false,
		hour: "2-digit",
		minute: "2-digit",
	});
}



/**
 * @param {string} seatingName
 * @returns {string}
 * */
export function formatSeating(seatingName) {
	if (seatingName === "default") {
		return "Standard Seating";
	}
	return seatingName;
}


/**
 * @returns {QueryFormOption[]}
 */
export function generatePartyOptions() {
	const result = [];

	for (let i = 1; i <= 10; i++) {
		result.push({
			name: i === 1 ? "1 Person" : `${i} People`,
			value: i.toString(),
		});
	}

	return result;
}
